import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from '@/components/ui/dropdown';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2, CalendarDays, LinkIcon } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { PageLoader } from '@/components/ui/page-loader';
import { BASE_URL } from '../config/constants';
import { toast } from 'sonner';
import { usePlan } from '@/context/PlanContext';
import { PlanLimitBadge, GracePeriodBanner, PlanItemBadge } from '@/components/PlanComponents';
import SectionTextSettingsCard from '@/components/SectionTextSettingsCard';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface ToursEvent {
  _id: string;
  eventName: string;
  heading: string;
  subtitle?: string;
  description?: string;
  date: string;
  endDate?: string;
  venue: string;
  city: string;
  country: string;
  image: string;
  ctaText: string;
  ctaLink: string;
  eventStatus: 'Upcoming' | 'Active' | 'Completed' | 'Cancelled';
  computedStatus?: string;
  order: number;
  isActive: boolean;
  createdAt?: string;
}

interface SortableTableRowProps {
  event: ToursEvent;
  index: number;
  handleEdit: (event: ToursEvent) => void;
  handleToggleActive: (id: string, currentStatus: boolean) => void;
  setDeleteId: (id: string) => void;
  togglingId: string | null;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({
  event,
  index,
  handleEdit,
  handleToggleActive,
  setDeleteId,
  togglingId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: event._id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Upcoming':
        return 'bg-info-light text-info';
      case 'Active':
        return 'bg-success-light text-success';
      case 'Completed':
        return 'bg-secondary text-muted-foreground';
      case 'Cancelled':
        return 'bg-danger-light text-danger';
      default:
        return 'bg-secondary text-muted-foreground';
    }
  };

  const eventDate = new Date(event.date);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-muted/50 transition-colors">
      <TableCell className="select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="text-muted-foreground font-medium">{event.order + 1}</span>
        </div>
      </TableCell>
      <TableCell className="font-medium text-foreground">{event.eventName}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{event.venue}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{formattedDate}</TableCell>
      <TableCell>
        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${getStatusBadgeColor(event.computedStatus || event.eventStatus)}`}>
          {event.computedStatus || event.eventStatus}
        </span>
      </TableCell>
      <TableCell>
        {event.image && (
          <img 
            src={event.image} 
            alt="event" 
            className="w-20 h-12 object-cover rounded" 
          />
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Switch
            checked={event.isActive}
            onCheckedChange={() => handleToggleActive(event._id, event.isActive)}
            disabled={togglingId === event._id}
            aria-label="Toggle event visibility"
          />
          <span className={event.isActive ? 'status-label-active' : 'status-label-inactive'}>{event.isActive ? 'Active' : 'Inactive'}</span>
        </div>
      </TableCell>
      <TableCell>
        <PlanItemBadge resourceType="toursEvents" index={index} />
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon-sm" 
            onClick={() => handleEdit(event)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button 
            variant="destructive" 
            size="icon-sm" 
            onClick={() => setDeleteId(event._id)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const emptyEvent: Omit<ToursEvent, '_id'> = {
  eventName: '',
  heading: '',
  subtitle: '',
  description: '',
  date: '',
  endDate: '',
  venue: '',
  city: '',
  country: '',
  image: '',
  ctaText: '',
  ctaLink: '',
  eventStatus: 'Upcoming',
  order: 0,
  isActive: true
};

interface ToursEventManagerProps {
  domain: string;
}

const ToursEventManager: React.FC<ToursEventManagerProps> = ({ domain }) => {
  const { canAdd, openUpgradeModal, adjustUsage } = usePlan();
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState<ToursEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<ToursEvent, '_id'>>(emptyEvent);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [uploadStep, setUploadStep] = useState<'idle' | 'selecting' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [_sectionActive, setSectionActive] = useState(true);
  const [_sectionConfigLoading, setSectionConfigLoading] = useState(true);
  const [ticketUrl, setTicketUrl] = useState('');
  const [ticketUrlError, setTicketUrlError] = useState('');
  const [isFetchingEvent, setIsFetchingEvent] = useState(false);

  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchEvents = () => {
    setLoading(true);
    axios.get(`${BASE_URL}/api/tours-events`)
      .then(res => setEvents(res.data))
      .catch(err => {
        console.error('Error fetching events:', err);
        toast.error('Failed to load events');
        setEvents([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchSectionConfig = () => {
    setSectionConfigLoading(true);
    axios.get(`${BASE_URL}/api/tours-events/section-config/get`)
      .then(res => setSectionActive(res.data.sectionActive))
      .catch(err => {
        console.error('Error fetching section config:', err);
        setSectionActive(true);
      })
      .finally(() => setSectionConfigLoading(false));
  };

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const statusOptions = [
    { value: 'Upcoming', label: 'Upcoming' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ];

  const openCropperForExisting = () => {
    if (form.image) {
      fetch(form.image)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'event-image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          toast.error('Failed to load image for editing');
        });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validFormats = ['image/png', 'image/jpeg'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validFormats.includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      toast.error('Only .png and .jpeg files are allowed.');
      return;
    }

    if (file.size > maxSize) {
      setError('File size must be less than 5MB.');
      toast.error('File size must be less than 5MB.');
      return;
    }

    setError('');
    setSelectedFile(file);
    setUploadStep('cropping');
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!croppedImage) return;

    setUploading(true);
    try {
      const response = await fetch(croppedImage);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('file', blob, 'cropped-event.png');

      const res = await axios.post(BASE_URL + '/api/images/upload', formData);
      setForm((prev) => ({ ...prev, image: res.data.url }));
      resetUploadState();
      const isUpdate = form.image ? true : false;
      toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
    } catch (err) {
      setError('Image upload failed.');
      toast.error('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchSectionConfig();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [domain]);

  // Auto-open the "Add Event" dialog when navigated here from the dashboard
  // card (state.openAdd). Mirrors the Add button's plan-limit behaviour, then
  // clears the navigation state so it doesn't re-trigger on refresh/back.
  useEffect(() => {
    if (!(location.state as { openAdd?: boolean } | null)?.openAdd) return;
    navigate(location.pathname, { replace: true, state: null });
    if (!canAdd('toursEvents')) {
      openUpgradeModal('toursEvents');
    } else {
      setAddOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTicketUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTicketUrl(value);
    setTicketUrlError('');

    if (!value) {
      setForm(emptyEvent);
      return;
    }

    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      return; // still typing
    }

    // Always set ctaLink + default ctaText immediately
    setForm(prev => ({ ...prev, ctaLink: value, ctaText: prev.ctaText || 'Get Tickets' }));
    setIsFetchingEvent(true);

    try {
      const res = await axios.get(`${BASE_URL}/api/tours-events/scrape?url=${encodeURIComponent(value)}`);
      const d = res.data;
      setForm(prev => ({
        ...prev,
        eventName: d.eventName || prev.eventName,
        description: d.description || prev.description,
        date: d.date || prev.date,
        endDate: d.endDate || prev.endDate,
        venue: d.venue || prev.venue,
        city: d.city || prev.city,
        country: d.country || prev.country,
        image: d.image || prev.image,
        ctaLink: value,
        ctaText: prev.ctaText || 'Get Tickets',
      }));
      // If we got back at least an event name, clear any previous error
      if (d.eventName) setTicketUrlError('');
    } catch (err: any) {
      const serverMsg = err.response?.data?.message;
      setTicketUrlError(serverMsg || 'Auto-fill unavailable for this URL — the fields below are ready for manual entry.');
    } finally {
      setIsFetchingEvent(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/tours-events`, { ...form, order: events.length });
      handleCloseDialog(false);
      toast.success('Event added successfully!');
      adjustUsage('toursEvents', 1);

      if (response.data) {
        setEvents(prev => [...prev, response.data]);
      } else {
        fetchEvents();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add event';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (event: ToursEvent) => {
    setEditId(event._id);
    setForm({
      eventName: event.eventName,
      heading: event.heading,
      subtitle: event.subtitle || '',
      description: event.description || '',
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      endDate: event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '',
      venue: event.venue,
      city: event.city,
      country: event.country,
      image: event.image,
      ctaText: event.ctaText,
      ctaLink: event.ctaLink,
      eventStatus: event.eventStatus,
      order: event.order,
      isActive: event.isActive
    });
    // Pre-populate ticket URL from existing ctaLink so fields are visible
    setTicketUrl(event.ctaLink || '');
    setTicketUrlError('');
    setEditOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const currentEditId = editId;
    try {
      const response = await axios.put(`${BASE_URL}/api/tours-events/${editId}`, form);
      handleCloseDialog(true);
      toast.success('Event updated successfully!');

      if (response.data) {
        setEvents(prev => prev.map(event =>
          event._id === currentEditId ? { ...event, ...response.data } : event
        ));
      } else {
        fetchEvents();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update event';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    setTogglingId(id);
    // Optimistic update
    setEvents(prev => prev.map(event =>
      event._id === id ? { ...event, isActive: !currentStatus } : event
    ));
    try {
      await axios.patch(`${BASE_URL}/api/tours-events/${id}/toggle-status`);
      toast.success(currentStatus ? 'Event deactivated successfully!' : 'Event activated successfully!');
    } catch (err) {
      // Revert on error
      setEvents(prev => prev.map(event =>
        event._id === id ? { ...event, isActive: currentStatus } : event
      ));
      toast.error('Failed to update event status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const deletedEvent = events.find(event => event._id === deleteId);
    // Optimistic removal
    setEvents(prev => prev.filter(event => event._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/tours-events/${deleteId}`);
      toast.success('Event deleted successfully!');
      adjustUsage('toursEvents', -1);

    } catch (err) {
      // Revert on error
      if (deletedEvent) {
        setEvents(prev => [...prev, deletedEvent].sort((a, b) => a.order - b.order));
      }
      toast.error('Failed to delete event');
    }
  };

  const handleCloseDialog = (isEdit: boolean) => {
    setForm(emptyEvent);
    setEditId(null);
    setError('');
    setTicketUrl('');
    setTicketUrlError('');
    resetUploadState();
    if (isEdit) {
      setEditOpen(false);
    } else {
      setAddOpen(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = events.findIndex(e => e._id === active.id);
      const newIndex = events.findIndex(e => e._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const eventToMove = events[oldIndex];

        try {
          await axios.put(`${BASE_URL}/api/tours-events/${eventToMove._id}/reorder`, {
            newIndex: newIndex
          });
          fetchEvents();
          toast.success('Event reordered successfully!');
        } catch (err) {
          toast.error('Failed to reorder event');
        }
      }
    }
  };

  return (
    <div>
      <GracePeriodBanner />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="page-title">Tours & Events Management</h2>
          <PlanLimitBadge resourceType="toursEvents" />
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={(e) => {
                if (!canAdd('toursEvents')) {
                  e.preventDefault();
                  e.stopPropagation();
                  openUpgradeModal('toursEvents');
                }
              }}
            >
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Event</DialogTitle>
              <DialogDescription>Create a new tour or event</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleAdd} className="space-y-5">
              {/* Ticketing URL — paste to auto-fill all fields */}
              <div className="space-y-1.5">
                <Label htmlFor="ticketUrl" className="form-label">Ticketing URL</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    id="ticketUrl"
                    placeholder="Paste a Ticketing platform link..."
                    value={ticketUrl}
                    onChange={handleTicketUrlChange}
                    className="pl-10 pr-3.5"
                  />
                </div>
                {ticketUrlError && (
                  <p className="text-sm text-destructive">{ticketUrlError}</p>
                )}
                {!isFetchingEvent && !ticketUrlError && ticketUrl && form.ctaLink && (
                  <p className="text-sm text-success">Event details loaded — review and edit below.</p>
                )}
              </div>

              {ticketUrl && isFetchingEvent && (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
                  <Spinner size="xl" />
                  <p className="text-sm">Fetching event details...</p>
                </div>
              )}

              {/* All fields revealed once a URL is pasted */}
              {ticketUrl && !isFetchingEvent && (
                <>
              <div className="form-group">
                <Label className="form-label">Event Name *</Label>
                <Input
                  id="eventName"
                  name="eventName"
                  value={form.eventName}
                  onChange={handleFormChange}
                  placeholder="e.g., World Tour 2024"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Event details and description"
                  rows={3}
                  className="h-24"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Event Date *</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={form.date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Venue *</Label>
                <Input
                  id="venue"
                  name="venue"
                  value={form.venue}
                  onChange={handleFormChange}
                  placeholder="e.g., Madison Square Garden"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">CTA Text *</Label>
                <Input
                  id="ctaText"
                  name="ctaText"
                  value={form.ctaText}
                  onChange={handleFormChange}
                  placeholder="e.g., Get Tickets"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">CTA Link *</Label>
                <Input 
                  name="ctaLink" 
                  value={(form.ctaLink || '').replace(/^https?:\/\//, '')} 
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/^https?:\/\//, '');
                    handleFormChange({ ...e, target: { ...e.target, name: 'ctaLink', value: rawValue } } as any);
                  }}
                  required 
                  prefix="https://"
                  placeholder="e.g., tickets.com"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Event Image (16:9)</Label>
                {uploadStep === 'idle' ? (
                  !form.image ? (
                    <div className="w-full">
                      <Card
                        className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={openFileUpload}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: PNG, JPEG (max 5 MB) | Aspect ratio: 16:9
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={openCropperForExisting}
                      >
                        <div 
                          className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                          style={{
                            backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                            backgroundSize: '16px 16px',
                            backgroundPosition: '0 0, 8px 8px',
                            backgroundRepeat: 'repeat'
                          }}
                        >
                          <img 
                            src={form.image} 
                            alt="uploaded" 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="hidden"
                      />
                    </div>
                  )
                ) : uploadStep === 'cropping' && selectedFile ? (
                  <div className="space-y-2">
                    <Card className="flex flex-col items-center">
                      <div className="w-full border-b p-3">
                        <p className="text-sm font-medium text-foreground text-center">Adjust crop area (16:9 Aspect) - Drag to move, edges to resize</p>
                      </div>
                      <div className="w-full flex justify-center py-3">
                        <Cropper
                          file={selectedFile}
                          aspect={16 / 9}
                          maxImageSize={5 * 1024 * 1024}
                          keepSelection={true}
                          onCrop={handleCropComplete}
                          onCropRef={cropperRef}
                        />
                      </div>
                    </Card>
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        onClick={async () => {
                          await cropperRef.current?.applyCrop();
                        }}
                        disabled={uploading}
                        size="sm"
                        loading={uploading}
                        loadingText="Uploading..."
                      >
                        {form.image ? 'Update' : 'Upload'}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={resetUploadState}
                        disabled={uploading}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <Label className="form-label">Event Status *</Label>
                <Dropdown
                  options={statusOptions}
                  value={form.eventStatus}
                  onValueChange={(val) => setForm(prev => ({ ...prev, eventStatus: val as ToursEvent['eventStatus'] }))}
                  placeholder="Select Status"
                />
              </div>
              {error && <div className="text-danger">{error}</div>}
              <Button type="submit" loading={saving} loadingText="Adding...">Add Event</Button>
                </>
              )}
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <SectionTextSettingsCard sectionKey="toursEvents" />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
            <DialogDescription>Update event details</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdate} className="space-y-5">
              {/* Ticketing URL — paste to re-fetch or change ticket link */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-ticketUrl" className="form-label">Ticketing URL</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    id="edit-ticketUrl"
                    placeholder="Paste a ticketing link to update..."
                    value={ticketUrl}
                    onChange={handleTicketUrlChange}
                    className="pl-10 pr-3.5"
                  />
                </div>
                {ticketUrlError && (
                  <p className="text-sm text-destructive">{ticketUrlError}</p>
                )}
              </div>

              {ticketUrl && isFetchingEvent && (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
                  <Spinner size="xl" />
                  <p className="text-sm">Fetching event details...</p>
                </div>
              )}
              {ticketUrl && !isFetchingEvent && (
                <>
            <div className="form-group">
              <Label className="form-label">Event Name *</Label>
              <Input
                id="eventName"
                name="eventName"
                value={form.eventName}
                onChange={handleFormChange}
                placeholder="e.g., World Tour 2024"
                required
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleFormChange}
                placeholder="Event details and description"
                rows={3}
                className="h-24"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Event Date *</Label>
              <Input
                id="date"
                name="date"
                type="date"
                value={form.date}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Venue *</Label>
              <Input
                id="venue"
                name="venue"
                value={form.venue}
                onChange={handleFormChange}
                placeholder="e.g., Madison Square Garden"
                required
              />
            </div>
            <div className="form-group">
              <Label className="form-label">CTA Text *</Label>
              <Input
                id="ctaText"
                name="ctaText"
                value={form.ctaText}
                onChange={handleFormChange}
                placeholder="e.g., Get Tickets"
                required
              />
            </div>
            <div className="form-group">
              <Label className="form-label">CTA Link *</Label>
              <Input 
                name="ctaLink" 
                value={(form.ctaLink || '').replace(/^https?:\/\//, '')} 
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/^https?:\/\//, '');
                  handleFormChange({ ...e, target: { ...e.target, name: 'ctaLink', value: rawValue } } as any);
                }}
                required 
                prefix="https://"
                placeholder="e.g., tickets.com"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Event Image (16:9)</Label>
              {uploadStep === 'idle' ? (
                !form.image ? (
                  <div className="w-full">
                    <Card
                      className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={openFileUpload}
                    >
                      <div className="grid space-y-3">
                        <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                          <Upload className="size-5" />
                          <div>
                            Drop image here or{" "}
                            <Button
                              type="button"
                              variant="link"
                              className="text-primary p-0 h-auto font-normal"
                              onClick={(e) => {
                                e.stopPropagation();
                                openFileUpload();
                              }}
                            >
                              browse
                            </Button>{" "}
                            to upload
                          </div>
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg"
                        onChange={handleFileChange}
                      />
                      <span className="text-xs text-muted-foreground mt-1 block">
                        Supported: PNG, JPEG (max 5 MB) | Aspect ratio: 16:9
                      </span>
                    </Card>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Card
                      className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={openCropperForExisting}
                    >
                      <div 
                        className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                        style={{
                          backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                          backgroundSize: '16px 16px',
                          backgroundPosition: '0 0, 8px 8px',
                          backgroundRepeat: 'repeat'
                        }}
                      >
                        <img 
                          src={form.image} 
                          alt="uploaded" 
                          className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                          title="Click to edit image"
                        />
                      </div>
                      <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                        <Upload className="size-4" />
                        <span>Click to edit or re-upload</span>
                      </div>
                    </Card>
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={openFileUpload}
                      >
                        <RefreshCw className="size-3.5" />
                        Change
                      </Button>
                      <Button 
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-danger"
                        onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                      >
                        <Trash2 className="size-3.5" />
                        Remove
                      </Button>
                    </div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      className="hidden"
                    />
                  </div>
                )
              ) : uploadStep === 'cropping' && selectedFile ? (
                <div className="space-y-2">
                  <Card className="flex flex-col items-center">
                    <div className="w-full border-b p-3">
                      <p className="text-sm font-medium text-foreground text-center">Adjust crop area (16:9 Aspect) - Drag to move, edges to resize</p>
                    </div>
                    <div className="w-full flex justify-center py-3">
                      <Cropper
                        file={selectedFile}
                        aspect={16 / 9}
                        maxImageSize={5 * 1024 * 1024}
                        keepSelection={true}
                        onCrop={handleCropComplete}
                        onCropRef={cropperRef}
                      />
                    </div>
                  </Card>
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      onClick={async () => {
                        await cropperRef.current?.applyCrop();
                      }}
                      disabled={uploading}
                      size="sm"
                      loading={uploading}
                      loadingText="Uploading..."
                    >
                      {form.image ? 'Update' : 'Upload'}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={resetUploadState}
                      disabled={uploading}
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group">
              <Label className="form-label">Event Status *</Label>
              <Dropdown
                options={statusOptions}
                value={form.eventStatus}
                onValueChange={(val) => setForm(prev => ({ ...prev, eventStatus: val as ToursEvent['eventStatus'] }))}
                placeholder="Select Status"
              />
            </div>
            {error && <div className="text-danger">{error}</div>}
            <Button type="submit" loading={saving} loadingText="Saving...">Save Changes</Button>
                </>
              )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Event"
        description="Are you sure you want to delete this event? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />

      <div>
        {loading ? (
          <PageLoader label="Loading events..." />
        ) : events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <CalendarDays className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No events found</h3>
            <p className="empty-state-description">Create your first tour or event to get started</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Order</TableHead>
                  <TableHead>Event Name</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Event Status</TableHead>
                  <TableHead>Image</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext
                  items={events.map(e => e._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {events.map((event, idx) => (
                    <SortableTableRow
                      key={event._id}
                      event={event}
                      index={idx}
                      handleEdit={handleEdit}
                      handleToggleActive={handleToggleActive}
                      setDeleteId={setDeleteId}
                      togglingId={togglingId}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default ToursEventManager;
